import React, { useMemo } from "react";
import { Typography as MuiTypography } from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { z } from "zod";

import useQueryParams from "../../hooks/useQueryParams";
import useDropdown from "../../hooks/useDropdown";
import FiltersForm from "../../components/FiltersForm";
import CorrelationChartContainer from "../../components/charts/correlationChart/CorrelationChartContainer";
import { usePostHeight } from "../../hooks/usePostHeight";

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;
const Typography = styled(MuiTypography)(spacing);

const DEFAULTS = { locationids: ["79"], p1: 28, p2: 24 };
const schema = z.object({
  locationids: z
    .preprocess((v) => {
      if (Array.isArray(v)) return v;
      if (typeof v === "string") return v.split(",");
      return [];
    }, z.array(z.string()))
    .default(DEFAULTS.locationids),
  p1: z.coerce.number().default(DEFAULTS.p1),
  p2: z.coerce.number().default(DEFAULTS.p2),
  controls: z.preprocess(
    (val) => val === "true" || val === true,
    z.boolean().default(false)
  ),
  height: z.preprocess(
    (val) => (typeof val === "string" ? parseInt(val, 10) : val),
    z.number().default(400)
  ),
});

export default function CorrelationGraphEmbed() {
  usePostHeight();
  const { params, setParams } = useQueryParams(schema);

  const locOptions = useDropdown(
    "results-simple/download-dropdown-locs-active"
  );
  const parOptions = useDropdown(
    "results-simple/list-parameters-dropdown-simple-active"
  );

  const getParameterLabel = (paramId) => {
    const param = parOptions.find((p) => p.value === String(paramId));
    return param ? param.label : "Parameter";
  };

  const FILTERS = useMemo(
    () => [
      {
        type: "auto",
        multi: true,
        param: "locationids",
        label: "Location",
        default: params.locationids.map(String),
        options: locOptions,
      },
      {
        type: "auto",
        param: "p1",
        label: "Independent parameter (x-axis)",
        default: String(params.p1),
        options: parOptions,
      },
      {
        type: "auto",
        param: "p2",
        label: "Dependent parameter (y-axis)",
        default: String(params.p2),
        options: parOptions,
      },
    ],
    [locOptions, parOptions, params]
  );

  return (
    <>
      <main>
        {params.controls && (
          <Section>
            <Typography variant="h5" component="h2" gutterBottom>
              Filters
            </Typography>
            <FiltersForm config={FILTERS} onChange={setParams} />
          </Section>
        )}

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            {getParameterLabel(params.p1)} vs {getParameterLabel(params.p2)}
          </Typography>
          <CorrelationChartContainer
            locationids={params.locationids}
            p1={params.p1}
            p2={params.p2}
            height={params.height}
            setParams={() =>
              setParams({
                ...params,
                p1: params.p2,
                p2: params.p1,
              })
            }
          />
        </Section>
      </main>
    </>
  );
}
